import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export interface TodoCategoryCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (payload: { name: string; color: string; categoryId?: string | null }) => void;
  initialName?: string;
  initialColor?: string;
  /** 수정 중인 카테고리 id. 있으면 새로 만들지 않고 이 항목을 갱신한다. */
  categoryId?: string | null;
  /** false면 이름은 고정(일반 카테고리 색만 변경) */
  nameEditable?: boolean;
  /** false면 색상 고정 */
  colorEditable?: boolean;
  title?: string;
  confirmLabel?: string;
}

const DEFAULT_COLOR = '#2e7d32';

const TodoCategoryCreateModal: React.FC<TodoCategoryCreateModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  initialName = '',
  initialColor = DEFAULT_COLOR,
  categoryId = null,
  nameEditable = true,
  colorEditable = true,
  title = '카테고리 만들기',
  confirmLabel = '추가',
}) => {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const nameInputRef = useRef<HTMLInputElement | null>(null);
  const categoryIdRef = useRef<string | null>(categoryId);
  const colorWrapRef = useRef<HTMLDivElement | null>(null);
  const [name, setName] = useState('');
  const [color, setColor] = useState(DEFAULT_COLOR);
  const [error, setError] = useState('');
  const [colorHintHover, setColorHintHover] = useState(false);
  const [colorHintPinned, setColorHintPinned] = useState(false);
  const showColorHint = !colorEditable && (colorHintHover || colorHintPinned);

  useEffect(() => {
    if (isOpen) {
      categoryIdRef.current = categoryId ?? null;
      setName(initialName);
      setColor(initialColor);
      setError('');
      setColorHintHover(false);
      setColorHintPinned(false);
    }
  }, [isOpen, initialName, initialColor, categoryId]);

  useEffect(() => {
    if (!colorHintPinned) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (colorWrapRef.current?.contains(e.target as Node)) return;
      setColorHintPinned(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setColorHintPinned(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [colorHintPinned]);

  useEffect(() => {
    if (!isOpen) return;
    window.setTimeout(() => {
      if (nameEditable) {
        nameInputRef.current?.focus();
        nameInputRef.current?.select();
      } else if (colorEditable) {
        document.getElementById('cp-todo-cat-color')?.focus();
      }
    }, 0);
  }, [isOpen, nameEditable, colorEditable]);

  const handleSubmit = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('카테고리 이름을 입력해 주세요.');
      return;
    }
    if (trimmed.length > 60) {
      setError('이름은 60자 이내로 해 주세요.');
      return;
    }
    if (!/^#[0-9A-Fa-f]{6}$/.test(color)) {
      setError('색상 형식이 올바르지 않습니다.');
      return;
    }
    onConfirm({ name: trimmed, color, categoryId: categoryIdRef.current });
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="cp-modal-backdrop" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className="cp-modal cp-todo-note-category-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        ref={contentRef}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h3>{title}</h3>
        <div className="cp-modal-body">
          {error ? <p className="cp-profile-message">{error}</p> : null}
          <div className="cp-todo-note-modal-field">
            <label htmlFor="cp-todo-cat-name">이름</label>
            <input
              id="cp-todo-cat-name"
              ref={nameInputRef}
              type="text"
              className="cp-profile-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="예: 업무"
              maxLength={60}
              autoComplete="off"
              readOnly={!nameEditable}
              disabled={!nameEditable}
              aria-readonly={!nameEditable}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
            />
          </div>
          <div className="cp-todo-note-modal-field">
            <label>색상</label>
            <div
              ref={colorWrapRef}
              className={
                'cp-todo-note-color-picker-inline' +
                (!colorEditable ? ' is-locked' : '')
              }
              onMouseEnter={() => {
                if (!colorEditable) setColorHintHover(true);
              }}
              onMouseLeave={() => setColorHintHover(false)}
              onClick={() => {
                if (!colorEditable) setColorHintPinned((v) => !v);
              }}
            >
              <input
                id="cp-todo-cat-color"
                type="color"
                className="cp-todo-note-color-picker-input"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                disabled={!colorEditable}
                aria-label="카테고리 색"
                aria-describedby={showColorHint ? 'cp-todo-cat-color-hint' : undefined}
              />
              {showColorHint ? (
                <div
                  id="cp-todo-cat-color-hint"
                  className="cp-todo-note-color-lock-mini"
                  role="tooltip"
                >
                  이 카테고리는 색상을 바꿀 수 없습니다.
                </div>
              ) : null}
            </div>
          </div>
        </div>
        <div className="cp-modal-footer">
          <button type="button" className="cp-btn-secondary" onClick={onClose}>
            취소
          </button>
          <button type="button" className="cp-btn-primary" onClick={handleSubmit}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TodoCategoryCreateModal;
